// src\components\externalEstimateURL\TradeOS\TradeCompliancePassportCard.jsx
import { Tooltip } from 'antd'
import { X } from 'lucide-react'
import React, { useState, useMemo } from 'react'

const CompliancePassportCard = ({ data, onRequestOUS, formData, setFormData }) => {
    const [modal, setModal] = useState(null)
    const [showFullSummary, setShowFullSummary] = useState(false)
    const [expandedItems, setExpandedItems] = useState({}) // Track which cards have expanded items

    const renderDocs = (str) => {
        const content = str.split(";").map((item, i) => <li key={i}>{item}</li>)
        return <ul className="list-disc list-inside">{content}</ul>
    }

    const renderDocsShort = (str, item) => {
        const content = str.split(";")
        return (
            <p>
                {`${content[0]}`}
                <span
                    onClick={() => setModal(item)}
                    className="text-sm font-medium underline cursor-pointer"
                >
                    {" "}
                    {content.length > 1 ? `+ ${content.length - 1} more` : ""}
                </span>
            </p>
        )
    }

    const renderSummary = (summary) => {
        if (!summary) return null
        const isLong = summary.length > 350
        const displayText = showFullSummary
            ? summary
            : summary.substring(0, 350) + (isLong ? "..." : "")

        return (
            <>
                <p className="text-sm">{displayText}</p>
                {isLong && (
                    <button
                        type="button"
                        onClick={() => setShowFullSummary((prev) => !prev)}
                        className="text-blue-600 text-sm font-medium hover:underline mt-1"
                    >
                        {showFullSummary ? "Read less" : "Read more"}
                    </button>
                )}
            </>
        )
    }

    const toggleItemExpansion = (idx) => {
        setExpandedItems(prev => ({
            ...prev,
            [idx]: !prev[idx]
        }))
    }

    // Flatten all compliance items across HS code groups and remove duplicates
    const flatCompliances = useMemo(() => {
        const allCompliances = []
        const uniqueMap = new Map()

        data.forEach((hsCodeGroup) => {
            const { HSCode, items = [], compliance = [] } = hsCodeGroup || {}

            compliance.forEach((c) => {
                const uniqueKey = `${c.new_content || ''}-${c.new_label || ''}-${c.ab_label || ''}-${c.new_label1 || ''}`

                if (!uniqueMap.has(uniqueKey)) {
                    const complianceItem = {
                        ...c,
                        __HSCode: HSCode,
                        __groupItems: [...items]
                    }
                    uniqueMap.set(uniqueKey, complianceItem)
                    allCompliances.push(complianceItem)
                } else {
                    const existingCompliance = uniqueMap.get(uniqueKey)
                    const existingItemNames = new Set(
                        existingCompliance.__groupItems.map(item => item.itemName)
                    )

                    items.forEach(item => {
                        if (!existingItemNames.has(item.itemName)) {
                            existingCompliance.__groupItems.push(item)
                            existingItemNames.add(item.itemName)
                        }
                    })
                }
            })
        })

        return allCompliances
    }, [data])

    return (
        <div className="w-full flex flex-col items-center justify-start gap-2">
            <div className="w-full grid grid-cols-1 md:grid-cols-3 items-stretch gap-5 text-wrap">
                {flatCompliances.map((item, idx) => {
                    const isExpanded = expandedItems[idx]
                    const hasMultipleItems = item.__groupItems && item.__groupItems.length > 1
                    const displayItems = isExpanded ? item.__groupItems : item.__groupItems?.slice(0, 1) || []

                    return (
                        <div
                            key={item.id || idx}
                            className="border rounded-xl p-3 flex flex-col items-center justify-between gap-2 shadow-lg hover:shadow-2xl transition-all"
                        >
                            <div className="w-full">
                                <div className="flex justify-between items-start border-b pb-2 mb-2">
                                    <div className="flex items-start justify-between w-full gap-2 font-medium">
                                        <div className="flex flex-col font-semibold pr-3">
                                            {item?.new_label?.length > 40 ? (
                                                <Tooltip title={item.new_label}>
                                                    <span>{item.new_label.slice(0, 40) + "..."}</span>
                                                </Tooltip>
                                            ) : (
                                                <span>{item.new_label}</span>
                                            )}
                                        </div>
                                        <div className="w-fit flex justify-end">
                                            <div className="px-4 py-2 text-sm text-nowrap font-semibold rounded-lg bg-gradient-to-r text-white from-amber-600 to-amber-400 shine w-fit">
                                                {item.ab_label}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-2">
                                    <div className="w-full border p-2 flex flex-col gap-2 rounded-lg text-sm">
                                        <div className="flex items-center justify-between">
                                            <span className='text-nowrap font-medium'>Required for:</span>
                                            {hasMultipleItems && (
                                                <button
                                                    type="button"
                                                    onClick={() => toggleItemExpansion(idx)}
                                                    className="text-blue-600 text-xs font-medium hover:underline"
                                                >
                                                    {isExpanded ? 'Show less' : `+${item.__groupItems.length - 1} more`}
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex flex-row items-center flex-wrap gap-1">
                                            {displayItems.map((itm, i) => (
                                                <p key={i} className='bg-gray-100 rounded px-2 py-1 text-sm'>
                                                    {itm.itemName}
                                                </p>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="w-fit border p-2 font-medium flex items-start gap-2 rounded-lg text-sm">
                                        <span className='text-nowrap'>Authority:</span>
                                        <span className="font-normal">{item.new_label1}</span>
                                    </div>
                                    <div className="w-fit border p-2 font-medium flex items-start gap-2 rounded-lg text-sm">
                                        <span className='text-nowrap'>Lead Time:</span>
                                        <span className="font-normal">{item.new_label2}</span>
                                    </div>
                                    <div className="w-fit border p-2 font-medium flex items-start gap-2 rounded-lg text-sm">
                                        <span className='text-nowrap'>Docs:</span>
                                        <span className="font-normal">{renderDocsShort(item.new_label3, item)}</span>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setModal(item)}
                                type="button"
                                className="w-full text-black bg-gray-200 flex items-center justify-center gap-2 rounded-lg p-1 font-medium hover:bg-gray-300 transition-colors"
                            >
                                Read More
                            </button>
                        </div>
                    )
                })}
            </div>

            {modal && (
                <div
                    onClick={() => {
                        setModal(null)
                        setShowFullSummary(false)
                    }}
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-lg w-full max-w-4xl p-4 flex flex-col items-center justify-center gap-4 max-h-[90vh] overflow-hidden"
                    >
                        <div className="flex flex-row items-center justify-between w-full border-b pb-2">
                            <div className="flex items-center gap-2">
                                <div className="px-4 py-2 font-semibold rounded-lg text-sm bg-gradient-to-r text-white from-amber-600 to-amber-400">
                                    {modal.ab_label}
                                </div>
                                <div className="flex flex-col font-semibold">
                                    <span>{modal.new_label}</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setModal(null)
                                    setShowFullSummary(false)
                                }}
                                className="p-1 rounded hover:bg-gray-200"
                            >
                                <X />
                            </button>
                        </div>

                        <div className="w-full grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto border-b pb-2">
                            <div className="w-full border col-span-2 shadow-lg flex flex-col items-start justify-center p-4 rounded-lg">
                                <p className="font-semibold">Authority</p>
                                <p>{modal.new_label1}</p>
                            </div>

                            <div className="w-full border shadow-lg flex flex-col items-start justify-start p-4 rounded-lg">
                                <p className="font-semibold">Summary</p>
                                {renderSummary(modal.new_content)}
                            </div>

                            <div className="w-full border shadow-lg flex flex-col items-start justify-start p-4 rounded-lg">
                                <p className="font-semibold">Documents</p>
                                {renderDocs(modal.new_label3)}
                            </div>
                        </div>

                        <div className="flex justify-end w-full">
                            <button
                                className="border btnBg rounded-lg px-4 py-2 mt-2 text-black bg-gray-200"
                                onClick={() => {
                                    setFormData(prev => ({ ...prev, handling: "YES" }));
                                    onRequestOUS();
                                    setModal(null);
                                    setShowFullSummary(false);
                                }}
                            >
                                Request OUS Handling
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default CompliancePassportCard